import { useMemo } from "react";

import {
  DERIVED_CERTIFICATION_TARGETS,
} from "./interpolantDerivedMetrics";
import {
  PRESET_QUERIES,
  evaluateInterpolantChain,
  type EvaluationResult,
} from "./interpolantProvenanceModel";
import "./InterpolantDerivedPlot.css";

const PLOT = {
  width: 720,
  height: 320,
  padLeft: 62,
  padRight: 32,
  padTop: 30,
  padBottom: 50,
};

type PlotPoint = {
  id: string;
  intent: string;
  isCurrent?: boolean;
  label: string;
  mode: EvaluationResult["mode"];
  provenanceMargin: number;
  x: number;
  y: number;
};

function formatNumber(value: number, digits = 1) {
  return value.toFixed(digits).replace(/\.?0+$/, "");
}

function formatSigned(value: number, digits = 1) {
  const formatted = formatNumber(Math.abs(value), digits);
  return `${value >= 0 ? "+" : "-"}${formatted}`;
}

function formatMargin(value: number) {
  return `${value >= 0 ? "+" : ""}${Math.round(value * 100)}%`;
}

function scale(value: number, min: number, max: number, outMin: number, outMax: number) {
  const span = max - min || 1;
  return outMin + ((value - min) / span) * (outMax - outMin);
}

function pointFrom(id: string, label: string, intent: string, evaluation: EvaluationResult, isCurrent = false): PlotPoint {
  return {
    id,
    intent,
    isCurrent,
    label,
    mode: evaluation.mode,
    provenanceMargin: evaluation.provenanceMargin,
    x: evaluation.derivedMetrics.ld,
    y: evaluation.derivedMetrics.certificationMargin,
  };
}

export function InterpolantDerivedPlot({
  currentEvaluation,
  onSelectPreset,
  selectedPreset,
}: {
  currentEvaluation: EvaluationResult;
  onSelectPreset: (id: string) => void;
  selectedPreset: string;
}) {
  const scenarioPoints = useMemo(
    () =>
      PRESET_QUERIES.map((query) =>
        pointFrom(query.id, query.label, query.intent, evaluateInterpolantChain(query.input)),
      ),
    [],
  );
  const currentPoint = pointFrom("current", "Current replay", "Current 12D condition in the audit inspector.", currentEvaluation, true);
  const visibleScenarioPoints = scenarioPoints.filter((point) => point.id !== selectedPreset);
  const allPoints = [...scenarioPoints, currentPoint];
  const targetX = DERIVED_CERTIFICATION_TARGETS.requiredLd;
  const targetY = DERIVED_CERTIFICATION_TARGETS.requiredCertificationMargin;
  const xValues = [...allPoints.map((point) => point.x), targetX];
  const yValues = [...allPoints.map((point) => point.y), targetY];
  const xMin = Math.min(...xValues) - 0.7;
  const xMax = Math.max(...xValues) + 0.7;
  const yMin = Math.min(...yValues) - 8;
  const yMax = Math.max(...yValues) + 8;
  const toX = (value: number) => scale(value, xMin, xMax, PLOT.padLeft, PLOT.width - PLOT.padRight);
  const toY = (value: number) => scale(value, yMin, yMax, PLOT.height - PLOT.padBottom, PLOT.padTop);

  return (
    <section className="ipd-panel ipd-derived-panel" data-testid="derived-quantity-plot" aria-label="Downstream derived quantity plot">
      <div className="ipd-panel-header ipd-derived-header">
        <div>
          <div className="ipd-panel-title">Downstream derived quantities</div>
          <div className="ipd-panel-subtitle">Fully derived artifacts downstream of the CFD response surrogate, plotted in requirement-margin output space.</div>
        </div>
        <div className="ipd-derived-current">
          <span>Current provenance margin</span>
          <strong className={currentEvaluation.mode}>{formatMargin(currentEvaluation.provenanceMargin)}</strong>
        </div>
      </div>
      <div className="ipd-derived-body">
        <div className="ipd-derived-chart">
          <svg viewBox={`0 0 ${PLOT.width} ${PLOT.height}`} role="img" aria-label="L/D ratio versus requirement margin">
            <defs>
              <linearGradient id="ipd-derived-pass" x1="0%" x2="100%" y1="100%" y2="0%">
                <stop offset="0%" stopColor="#62c39a" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#5ec8e0" stopOpacity="0.13" />
              </linearGradient>
            </defs>
            <rect
              className="ipd-derived-pass-region"
              x={toX(targetX)}
              y={PLOT.padTop}
              width={PLOT.width - PLOT.padRight - toX(targetX)}
              height={toY(targetY) - PLOT.padTop}
            />
            {[0.25, 0.5, 0.75].map((tick) => (
              <g key={tick}>
                <line className="ipd-derived-grid" x1={scale(tick, 0, 1, PLOT.padLeft, PLOT.width - PLOT.padRight)} x2={scale(tick, 0, 1, PLOT.padLeft, PLOT.width - PLOT.padRight)} y1={PLOT.padTop} y2={PLOT.height - PLOT.padBottom} />
                <line className="ipd-derived-grid" x1={PLOT.padLeft} x2={PLOT.width - PLOT.padRight} y1={scale(tick, 0, 1, PLOT.height - PLOT.padBottom, PLOT.padTop)} y2={scale(tick, 0, 1, PLOT.height - PLOT.padBottom, PLOT.padTop)} />
              </g>
            ))}
            <line className="ipd-derived-threshold" x1={toX(targetX)} x2={toX(targetX)} y1={PLOT.padTop} y2={PLOT.height - PLOT.padBottom} />
            <line className="ipd-derived-threshold" x1={PLOT.padLeft} x2={PLOT.width - PLOT.padRight} y1={toY(targetY)} y2={toY(targetY)} />
            <text className="ipd-derived-label" x={PLOT.padLeft} y={PLOT.padTop - 10}>Requirement margin (pts)</text>
            <text className="ipd-derived-label" x={toX(targetX) + 8} y={PLOT.padTop + 18}>L/D target {formatNumber(targetX, 1)}</text>
            <text className="ipd-derived-label" x={PLOT.padLeft + 10} y={toY(targetY) - 8}>requirement threshold 0 pts</text>
            <text className="ipd-derived-axis" x={PLOT.padLeft} y={PLOT.height - 15}>L/D ratio</text>

            {visibleScenarioPoints.map((point) => (
              <g className={`ipd-derived-point ${point.mode}`} key={point.id} transform={`translate(${toX(point.x)} ${toY(point.y)})`}>
                <circle r={6} />
                <text x={10} y={-7}>{point.label}</text>
                <text className="ipd-derived-margin" x={10} y={8}>margin {formatMargin(point.provenanceMargin)}</text>
              </g>
            ))}

            <g className={`ipd-derived-point current ${currentPoint.mode}`} transform={`translate(${toX(currentPoint.x)} ${toY(currentPoint.y)})`}>
              <circle r={11} />
              <circle r={4} />
              <line x1={-17} x2={-8} y1={0} y2={0} />
              <line x1={8} x2={17} y1={0} y2={0} />
              <line x1={0} x2={0} y1={-17} y2={-8} />
              <line x1={0} x2={0} y1={8} y2={17} />
              <text x={14} y={-13}>Current replay</text>
              <text className="ipd-derived-margin" x={14} y={3}>{currentPoint.mode} {formatMargin(currentPoint.provenanceMargin)}</text>
            </g>
          </svg>
        </div>
        <div className="ipd-derived-side">
          <div className="ipd-derived-metrics" aria-label="Current downstream artifact values">
            <div><span>L/D ratio</span><strong>{formatNumber(currentEvaluation.derivedMetrics.ld, 2)}</strong></div>
            <div><span>Lift reserve</span><strong>{formatSigned(currentEvaluation.derivedMetrics.liftReserve, 3)}</strong></div>
            <div><span>Pitch margin</span><strong>{formatSigned(currentEvaluation.derivedMetrics.pitchMargin, 3)}</strong></div>
            <div><span>Requirement margin</span><strong>{formatSigned(currentEvaluation.derivedMetrics.certificationMargin, 1)} pts</strong></div>
          </div>
          <div className="ipd-derived-scenarios" aria-label="Scenario reasons for hitting the map">
            {scenarioPoints.map((point) => (
              <button
                type="button"
                key={point.id}
                className="ipd-derived-scenario"
                data-testid={`derived-scenario-${point.id}`}
                aria-pressed={selectedPreset === point.id}
                onClick={() => onSelectPreset(point.id)}
              >
                <span>
                  <strong>{point.label}</strong>
                  <small>{point.intent}</small>
                </span>
                <em className={point.mode}>{point.mode}</em>
                <b>{formatMargin(point.provenanceMargin)}</b>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
